import React, { useEffect, useState } from "react";
import { getUserById } from "../utils/userUtils";
import MapComponent from "./MapComponent";
import { COLORS, LAYOUT } from "../constants";

// Clock Icon
const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Location Icon
const LocationIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// User Icon
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// CalendarCard: displays a single accepted calendar event with buyer/seller info
function CalendarCard({ event }) {
  // Default location (Milner Library)
  const mapCenter = { lat: 40.508, lng: -88.991 };
  const [buyer, setBuyer] = useState(null);
  const [seller, setSeller] = useState(null);

  useEffect(() => {
    async function fetchUsers() {
      if (event.buyerId) setBuyer(await getUserById(event.buyerId));
      if (event.sellerId) setSeller(await getUserById(event.sellerId));
    }
    fetchUsers();
  }, [event.buyerId, event.sellerId]);

  const cardStyles = {
    container: {
      background: '#fff',
      border: `2px solid ${COLORS.primary}15`,
      borderRadius: 16,
      boxShadow: '0 4px 16px rgba(206, 17, 38, 0.08)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'row',
      transition: 'all 0.3s ease',
      cursor: 'default',
      ':hover': {
        boxShadow: '0 8px 24px rgba(206, 17, 38, 0.15)',
        transform: 'translateY(-2px)'
      }
    },
    
    contentSection: {
      flex: 1,
      padding: LAYOUT.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: LAYOUT.spacing.md
    },
    
    title: {
      fontWeight: 700,
      color: COLORS.primary,
      fontSize: 22,
      marginBottom: LAYOUT.spacing.xs,
      display: 'flex',
      alignItems: 'center',
      gap: LAYOUT.spacing.sm
    },
    
    infoRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: LAYOUT.spacing.sm,
      fontSize: 15,
      color: COLORS.text.primary,
      lineHeight: 1.5
    },
    
    iconWrapper: {
      color: COLORS.primary,
      display: 'flex',
      alignItems: 'center',
      marginTop: 2,
      flexShrink: 0
    },
    
    label: {
      fontWeight: 600,
      color: COLORS.text.primary,
      marginRight: 4
    },
    
    value: {
      color: COLORS.text.secondary
    },
    
    participantsSection: {
      display: 'flex',
      gap: LAYOUT.spacing.lg,
      marginTop: LAYOUT.spacing.sm,
      paddingTop: LAYOUT.spacing.md,
      borderTop: `1px solid ${COLORS.border}`
    },
    
    participant: {
      display: 'flex',
      alignItems: 'center',
      gap: LAYOUT.spacing.xs,
      fontSize: 14,
      color: COLORS.text.secondary
    },
    
    participantIcon: {
      color: COLORS.primary,
      opacity: 0.7
    },
    
    mapSection: {
      flex: '0 0 280px',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: LAYOUT.spacing.md
    }
  };

  return (
    <div 
      style={cardStyles.container}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(206, 17, 38, 0.15)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(206, 17, 38, 0.08)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Content Section */}
      <div style={cardStyles.contentSection}>
        <div style={cardStyles.title}>
          {event.title || 'Meeting'}
        </div>
        
        <div style={cardStyles.infoRow}>
          <div style={cardStyles.iconWrapper}>
            <ClockIcon />
          </div>
          <div>
            <span style={cardStyles.label}>Time:</span>
            <span style={cardStyles.value}>{event.time}</span>
          </div>
        </div>
        
        <div style={cardStyles.infoRow}>
          <div style={cardStyles.iconWrapper}>
            <LocationIcon />
          </div>
          <div>
            <span style={cardStyles.label}>Location:</span>
            <span style={cardStyles.value}>{event.location?.address || "No location"}</span>
          </div>
        </div>
        
        <div style={cardStyles.participantsSection}>
          <div style={cardStyles.participant}>
            <div style={cardStyles.participantIcon}>
              <UserIcon />
            </div>
            <span style={cardStyles.label}>Buyer:</span>
            <span>{buyer ? buyer.name : "Loading..."}</span>
          </div>
          
          <div style={cardStyles.participant}>
            <div style={cardStyles.participantIcon}>
              <UserIcon />
            </div>
            <span style={cardStyles.label}>Seller:</span>
            <span>{seller ? seller.name : "Loading..."}</span>
          </div>
        </div>
      </div>
      
      {/* Map Section */}
      <div style={cardStyles.mapSection}>
        <MapComponent
          center={event.location?.lat && event.location?.lng ? { lat: event.location.lat, lng: event.location.lng } : mapCenter}
          marker={event.location?.lat && event.location?.lng ? { lat: event.location.lat, lng: event.location.lng } : undefined}
          width={250}
          height={200}
        />
      </div>
    </div>
  );
}

export default CalendarCard;
